import { supabase } from '../lib/supabase.js';

export const recommendationLetterService = {
  /**
   * Fetch all recommendation letters for a user
   * @param {string} userId - The user ID to fetch recommendations for
   * @returns {Promise<Array>} Array of recommendation letters
   */
  async getRecommendations(userId) {
    try {
      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching recommendations:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getRecommendations:', error);
      throw error;
    }
  },

  /**
   * Fetch a single recommendation by ID
   * @param {string} recommendationId - The recommendation ID
   * @returns {Promise<Object>} The recommendation
   */
  async getRecommendation(recommendationId) {
    try {
      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .select('*')
        .eq('id', recommendationId)
        .single();

      if (error) {
        console.error('Error fetching recommendation:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getRecommendation:', error);
      throw error;
    }
  },

  /**
   * Create a new recommendation letter request
   * @param {Object} recommendation - The recommendation data
   * @returns {Promise<Object>} The created recommendation
   */
  async createRecommendation(recommendation) {
    try {
      // Ensure colleges is an array
      const recommendationData = {
        ...recommendation,
        colleges: Array.isArray(recommendation.colleges) 
          ? recommendation.colleges 
          : recommendation.colleges ? [recommendation.colleges] : []
      };

      // Remove any empty date strings
      if (recommendationData.request_date === '') {
        delete recommendationData.request_date;
      }
      if (recommendationData.due_date === '') {
        delete recommendationData.due_date;
      }
      if (recommendationData.submitted_date === '') {
        delete recommendationData.submitted_date;
      }

      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .insert([recommendationData])
        .select()
        .single();

      if (error) {
        console.error('Error creating recommendation:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createRecommendation:', error);
      throw error;
    }
  },

  /**
   * Update an existing recommendation letter
   * @param {string} id - The recommendation ID
   * @param {Object} updates - The fields to update
   * @returns {Promise<Object>} The updated recommendation
   */
  async updateRecommendation(id, updates) {
    try {
      // Ensure colleges is an array if provided
      if (updates.colleges !== undefined) {
        updates.colleges = Array.isArray(updates.colleges) 
          ? updates.colleges 
          : updates.colleges ? [updates.colleges] : [];
      }

      // Handle email history updates
      if (updates.email_history) {
        // Fetch current email history
        const { data: currentRec } = await supabase
          .from('college_recommendation_letters')
          .select('email_history')
          .eq('id', id)
          .single();
        
        if (currentRec) {
          // Parse existing history if it's a string
          let existingHistory = currentRec.email_history || [];
          if (typeof existingHistory === 'string') {
            try {
              existingHistory = JSON.parse(existingHistory);
            } catch (e) {
              console.error('Failed to parse existing email_history:', e);
              existingHistory = [];
            }
          }
          // Ensure it's an array
          if (!Array.isArray(existingHistory)) {
            existingHistory = [];
          }
          updates.email_history = [...existingHistory, ...updates.email_history];
        }
      }

      // Add updated_at timestamp
      updates.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating recommendation:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateRecommendation:', error);
      throw error;
    }
  },

  /**
   * Save email content for a recommendation
   * @param {string} id - The recommendation ID
   * @param {Object} emailData - The email data to save
   * @returns {Promise<Object>} The updated recommendation
   */
  async saveEmailContent(id, emailData) {
    try {
      const { type, content, status = 'draft' } = emailData;
      
      // Prepare the update object
      const updates = {};
      const historyEntry = {
        type,
        content,
        status,
        timestamp: new Date().toISOString()
      };

      // Update the appropriate fields based on email type
      if (type === 'request') {
        updates.request_email_content = content;
        if (status === 'sent') {
          updates.request_email_sent_date = new Date().toISOString();
        }
      } else if (type === 'follow_up') {
        updates.follow_up_email_content = content;
        if (status === 'sent') {
          updates.follow_up_email_sent_date = new Date().toISOString();
        }
      }

      // Add to email history
      updates.email_history = [historyEntry];
      
      return await this.updateRecommendation(id, updates);
    } catch (error) {
      console.error('Error in saveEmailContent:', error);
      throw error;
    }
  },

  /**
   * Get email history for a recommendation
   * @param {string} id - The recommendation ID
   * @returns {Promise<Array>} The email history
   */
  async getEmailHistory(id) {
    try {
      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .select('email_history, request_email_content, request_email_sent_date, follow_up_email_content, follow_up_email_sent_date')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching email history:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getEmailHistory:', error);
      throw error;
    }
  },

  /**
   * Update the status of a recommendation
   * @param {string} id - The recommendation ID
   * @param {string} status - The new status
   * @returns {Promise<Object>} The updated recommendation
   */
  async updateStatus(id, status) {
    try {
      const updates = {
        status,
        updated_at: new Date().toISOString()
      };

      // If status is submitted, add submitted_date
      if (status === 'submitted') {
        updates.submitted_date = new Date().toISOString().split('T')[0];
      }

      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating recommendation status:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateStatus:', error);
      throw error;
    }
  },

  /**
   * Delete a recommendation letter
   * @param {string} id - The recommendation ID to delete
   * @returns {Promise<void>}
   */
  async deleteRecommendation(id) {
    try {
      const { error } = await supabase
        .from('college_recommendation_letters')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting recommendation:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteRecommendation:', error);
      throw error;
    }
  },

  /**
   * Get recommendation statistics for a user
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} Statistics object
   */
  async getRecommendationStats(userId) {
    try {
      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .select('status')
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching recommendation stats:', error);
        throw error;
      }

      const stats = {
        total: data?.length || 0,
        submitted: data?.filter(r => r.status === 'submitted').length || 0,
        inProgress: data?.filter(r => r.status === 'in_progress').length || 0,
        notRequested: data?.filter(r => r.status === 'not_requested').length || 0,
        declined: data?.filter(r => r.status === 'declined').length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error in getRecommendationStats:', error);
      throw error;
    }
  }
};

export default recommendationLetterService;